/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React from 'react';
import { Helmet } from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';
import { folderMap } from './seo.constants';

type SEOProps = {
  title?: string;
  description?: string;
  lang?: string;
  og_image?: string;
  og_title?: string;
  og_description?: string;
  twitter_image?: string;
  twitter_title?: string;
  twitter_description?: string;
  nofollow: boolean;
  noindex: boolean;
  slug?: string;
  authorized_roles: string[];
  alternates: {
    id: number;
    name: string;
    slug: string;
    published: boolean;
    fullSlug: string;
    isFolder: boolean;
    parentId: number;
  }[];
  websiteUrl: string;
};

function getAlternateUrl(locale: string) {
  const countryCode = locale || 'at-de';
  return folderMap[countryCode];
}

export const SEO = ({
  title,
  description,
  lang,
  og_image,
  og_title,
  og_description,
  twitter_image,
  twitter_title,
  twitter_description,
  slug,
  authorized_roles,
  alternates,
  nofollow,
  noindex,
  websiteUrl,
}: SEOProps): JSX.Element => {
  const { site } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          author
          url
          defaultLanguage
          twitterHandle
        }
      }
    }
  `);

  const isPrivate = authorized_roles !== undefined && authorized_roles.includes('authorized') ? 'yes' : 'no';

  const getAlternateLinks = () =>
    alternates.map(({ fullSlug }) => {
      // eslint-disable-next-line compat/compat
      const fullLocale = fullSlug.split('/');
      const splitLocale = fullLocale[0].split('-');

      const alternateUrl = getAlternateUrl(fullLocale[0]);

      return {
        href: `${alternateUrl}${fullSlug}`,
        rel: 'alternate',
        hreflang: `${splitLocale[1]}-${splitLocale[0]}`,
      };
    });

  const getRobots = () => {
    if (nofollow && noindex) {
      return 'nowfollow, noindex';
    }
    if (nofollow) {
      return 'nofollow';
    }
    if (noindex) {
      return 'noindex';
    }
    return '';
  };

  return (
    <Helmet
      htmlAttributes={{
        lang: lang === 'default' ? site.siteMetadata.defaultLanguage : lang,
      }}
      title={title}
      meta={[
        {
          name: 'description',
          content: description,
        },
        {
          property: 'og:image',
          content: og_image,
        },
        {
          property: 'og:title',
          content: og_title,
        },
        {
          property: 'og:description',
          content: og_description,
        },
        {
          property: 'og:type',
          content: 'website',
        },
        {
          property: 'og:url',
          content: `${websiteUrl}${slug}`,
        },
        {
          name: 'twitter:image',
          content: twitter_image,
        },
        {
          name: 'twitter:card',
          content: 'summary',
        },
        {
          name: 'twitter:creator',
          content: site.siteMetadata.twitterHandle,
        },
        {
          name: 'twitter:title',
          content: twitter_title,
        },
        {
          name: 'twitter:description',
          content: twitter_description,
        },
        {
          name: 'private',
          content: isPrivate,
        },
        ...(getRobots() && [{ name: 'robots', content: getRobots() }]),
      ]}
      link={[
        {
          href: `${websiteUrl}${slug}`,
          rel: 'canonical',
        },
        ...getAlternateLinks(),
      ]}
    />
  );
};

SEO.defaultProps = {
  lang: 'en',
  title: '',
  description: '',
  og_image: '',
  og_title: '',
  og_description: '',
  twitter_image: '',
  twitter_title: '',
  twitter_description: '',
  slug: '',
};
